/**
 * File: apps/qbench-cp/reactapp/src/shared/components/AnnouncementList.js
 *
 * Purpose: Displays dismissible announcement banners for the current customer.
 *
 * Scope: Shared AnnouncementList component shown near the top of the portal to broadcast
 *     messages, respecting per-user dismissal preferences.
 *
 * Tags: customer-portal, notifications
 *
 * Overview: Fetches announcements and the current user's preferences (viewed_announcements),
 *     filters out announcements the user has already dismissed, and renders the remainder as
 *     Bootstrap Alerts. Shows a loading spinner while announcements or preferences are still
 *     loading, and dismisses an announcement via useAnnouncementDismiss, tracking which
 *     announcement id is being dismissed to dim it during the request. Renders
 *     nothing if there is an error or no announcements remain to show.
 *
 * Exports: default export `AnnouncementList` component.
 */

import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { Alert, Spinner, Stack } from 'react-bootstrap';
import { useMeQuery } from 'shared/hooks/api/contacts';
import {
  useAnnouncements,
  useAnnouncementDismiss,
} from 'shared/hooks/api/announcements';

const Wrapper = styled.div`
  background: ${(p) => p.theme.color.palette.background.paper.portalbg};
  > div {
    max-width: 1410px;
  }
`;

const Banner = styled(Alert)`
  border: 1px solid #ebebeb;
  opacity: ${(p) => (p.$isDismissing ? 0.5 : 1)};

  .btn-close {
    top: 0.25rem;
    right: 0.5rem;
    padding: 0.5rem;
  }
`;

function Announcement({
  message,
  onDismiss,
  isDismissing,
  allow_dismiss: dismissible,
}) {
  return (
    <Banner
      variant="light"
      className="py-2 px-3 mb-2"
      $isDismissing={isDismissing}
      dismissible={dismissible && !isDismissing}
      onClose={onDismiss}
    >
      {message}
    </Banner>
  );
}

export default function AnnouncementList() {
  const {
    data: announcements,
    isLoading: isLoadingAnnouncements,
    isError,
  } = useAnnouncements();
  const [dismiss, { variables: dismissingId }] = useAnnouncementDismiss();
  const [{ preferences }, { isLoading: isLoadingPreferences }] = useMeQuery();

  const isLoading =
    isLoadingPreferences || isEmpty(preferences) || isLoadingAnnouncements;
  const { viewed_announcements: dismissedIds = [] } = preferences;
  const announcementsToShow = announcements.filter(
    (a) => !dismissedIds.includes(a.id),
  );

  function renderAnnouncements() {
    return announcementsToShow.map(({ id, ...props }, i) => (
      <Announcement
        key={i}
        onDismiss={() => dismiss(id)}
        isDismissing={dismissingId === id}
        {...props}
      />
    ));
  }

  if (isError || isEmpty(announcementsToShow)) return null;
  return (
    <Wrapper>
      <div className="mx-auto pt-4 fs-7 fw-normal">
        {isLoading ? (
          <Stack direction="horizontal" gap={2}>
            <Spinner animation="border" size="sm" variant="secondary" />
            <span className="text-secondary fs-8">
              Loading announcement messages...
            </span>
          </Stack>
        ) : (
          renderAnnouncements()
        )}
      </div>
    </Wrapper>
  );
}
